/**
 * Event List Item Component
 * Modern list item for agenda view with color bar, thumbnail, and attendee avatars
 */

import { Link } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  AvatarGroup,
  IconButton,
  Chip,
} from '@mui/material'
import {
  LocationOn,
  AccessTime,
  Check,
  QuestionMark,
  Close,
  Event as EventIcon,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import {
  CalendarEventDto,
  CalendarEventStatus,
  RsvpStatus,
  getEventStatusColor,
} from '@api/types/calendarEvent'
import { isEventPast } from '../../types/calendar-event-editor.types'

interface EventListItemProps {
  event: CalendarEventDto
  onRsvp?: (eventId: string, status: RsvpStatus) => void
  /** Preview attendees to display (if available) */
  attendeeAvatars?: Array<{ userId: string; profileImageUrl?: string; displayName: string }>
}

/**
 * Format time for display
 */
function formatEventTime(dateString: string, isAllDay: boolean): string {
  if (isAllDay) return 'All day'
  return format(parseISO(dateString), 'h:mm a')
}

/**
 * Get color for event status indicator bar
 */
function getEventIndicatorColor(event: CalendarEventDto): string {
  const isPast = isEventPast(event.endDateTime)
  if (event.status === CalendarEventStatus.Cancelled) return 'grey.400'
  if (isPast) return 'grey.500'
  if (event.status === CalendarEventStatus.Draft) return 'warning.main'

  // Use user's RSVP status color if available
  if (event.userRsvp) {
    switch (event.userRsvp.status) {
      case RsvpStatus.Attending:
        return 'success.main'
      case RsvpStatus.Tentative:
        return 'warning.main'
      case RsvpStatus.NotAttending:
        return 'error.main'
    }
  }

  return 'primary.main'
}

export function EventListItem({ event, onRsvp, attendeeAvatars }: EventListItemProps) {
  const isPast = isEventPast(event.endDateTime)
  const isCancelled = event.status === CalendarEventStatus.Cancelled
  const indicatorColor = getEventIndicatorColor(event)

  // Use provided attendee avatars or fallback to showing just the count
  const attendeeCount = event.attendanceStats?.attendingCount ?? 0

  return (
    <Paper
      component={Link}
      to={`/events/${event.id}`}
      sx={{
        display: 'flex',
        mb: 1.5,
        borderRadius: 2,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        opacity: isPast || isCancelled ? 0.7 : 1,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateX(4px)',
          '& .quick-actions': { opacity: 1 },
        },
      }}
    >
      {/* Color indicator bar */}
      <Box
        sx={{
          width: 4,
          bgcolor: indicatorColor,
          flexShrink: 0,
        }}
      />

      {/* Thumbnail */}
      {event.coverImageUrl ? (
        <Box
          sx={{
            width: 80,
            height: 80,
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box
            component="img"
            src={event.coverImageUrl}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: 80,
            height: 80,
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          <EventIcon sx={{ fontSize: 32, color: 'grey.400' }} />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
        {/* Title row */}
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              textDecoration: isCancelled ? 'line-through' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.title}
          </Typography>
          {event.status === CalendarEventStatus.Draft && (
            <Chip
              label="Draft"
              color={getEventStatusColor(event.status)}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {isCancelled && (
            <Chip
              label="Cancelled"
              color="error"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>

        {/* Time */}
        <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
          <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {formatEventTime(event.startDateTime, event.isAllDay)}
            {!event.isAllDay && ` - ${formatEventTime(event.endDateTime, false)}`}
          </Typography>
        </Stack>

        {/* Location */}
        {event.location?.name && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.location.name}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Attendee avatars / count */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {attendeeAvatars && attendeeAvatars.length > 0 ? (
          <AvatarGroup
            max={3}
            sx={{
              '& .MuiAvatar-root': {
                width: 24,
                height: 24,
                fontSize: '0.75rem',
                border: '2px solid',
                borderColor: 'background.paper',
              },
            }}
          >
            {attendeeAvatars.map((attendee) => (
              <Avatar
                key={attendee.userId}
                src={attendee.profileImageUrl}
                alt={attendee.displayName}
              >
                {attendee.displayName?.charAt(0) || '?'}
              </Avatar>
            ))}
          </AvatarGroup>
        ) : attendeeCount > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {attendeeCount} going
          </Typography>
        ) : null}
      </Box>

      {/* Quick actions (visible on hover) */}
      {onRsvp && !isPast && !isCancelled && (
        <Box
          className="quick-actions"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          <IconButton
            size="small"
            color={event.userRsvp?.status === RsvpStatus.Attending ? 'success' : 'default'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRsvp(event.id, RsvpStatus.Attending)
            }}
            title="Attending"
          >
            <Check fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={event.userRsvp?.status === RsvpStatus.Tentative ? 'warning' : 'default'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRsvp(event.id, RsvpStatus.Tentative)
            }}
            title="Maybe"
          >
            <QuestionMark fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={event.userRsvp?.status === RsvpStatus.NotAttending ? 'error' : 'default'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRsvp(event.id, RsvpStatus.NotAttending)
            }}
            title="Can't go"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Paper>
  )
}

export default EventListItem
